import { useState, useEffect, useContext } from 'react';
import api from '../../hooks/useApi';
import { AppContext } from '../../context';
import { useToast } from '../ui/Toast';
import OverheadCategorySelect from '../shared/OverheadCategorySelect';
import Modal from '../shared/Modal';

// ── Date helpers ──────────────────────────────────────────────────────────────

function getMondayOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function fmtFull(dateStr) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'short',
  });
}

function fmtShort(dateStr) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

const TODAY = new Date().toISOString().split('T')[0];

// ── Component ─────────────────────────────────────────────────────────────────

export default function TimesheetPage() {
  const { toast } = useToast();
  const { currentUser } = useContext(AppContext);

  const [weekStart, setWeekStart]     = useState(getMondayOfWeek());
  const [entries,   setEntries]       = useState([]);
  const [sowTasks,  setSowTasks]      = useState([]);
  const [overheads, setOverheads]     = useState([]);
  const [loading,   setLoading]       = useState(true);
  const [showAdd,   setShowAdd]       = useState(null); // date string
  const [form,      setForm]          = useState(blankForm(TODAY));
  const [saving,    setSaving]        = useState(false);
  const [allTeams,  setAllTeams]      = useState(false); // show all SOW tasks, not just my team

  // Reload SOW task list when the all-teams toggle changes
  useEffect(() => {
    const params = allTeams ? { all_teams: '1' } : {};
    api.get('timesheets/sow-tasks', params)
      .then(setSowTasks)
      .catch(() => {});
  }, [allTeams]);

  // Load overhead categories once
  useEffect(() => {
    api.get('overhead-categories').then(setOverheads).catch(() => {});
  }, []);

  // Weekly entries — reload when week changes
  useEffect(() => {
    setLoading(true);
    api.get('timesheets/my-week', { week_start: weekStart })
      .then(e => { setEntries(e); setLoading(false); })
      .catch(() => setLoading(false));
  }, [weekStart]);

  const weekDays    = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const totalHours  = entries.reduce((s, e) => s + (e.hours || 0), 0);
  const weekEnd     = addDays(weekStart, 6);
  const weekLabel   = `${fmtShort(weekStart)} – ${fmtShort(weekEnd)}`;
  const isThisWeek  = weekStart === getMondayOfWeek();

  const entriesByDay = weekDays.map(d => ({
    date:    d,
    entries: entries.filter(e => e.date === d),
    total:   entries.filter(e => e.date === d).reduce((s, e) => s + (e.hours || 0), 0),
  }));

  // Group SOW tasks by SOW reference for the <optgroup>
  const tasksBySow = sowTasks.reduce((acc, t) => {
    const key = `${t.sow_reference || '?'} — ${t.sow_detail || t.project_name || ''}`.trim();
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  const openAdd = (date) => {
    setForm(blankForm(date));
    setShowAdd(date);
  };

  const submitEntry = async () => {
    if (!form.hours || isNaN(parseFloat(form.hours)) || parseFloat(form.hours) <= 0) {
      toast.error('Please enter valid hours (e.g. 7.5)');
      return;
    }
    if (form.type === 'task' && !form.task_id) {
      toast.error('Please select a task');
      return;
    }
    if (form.type === 'overhead' && !form.overhead_category_id) {
      toast.error('Please select an overhead category');
      return;
    }
    setSaving(true);
    try {
      await api.post('time-entries', {
        task_id:              form.type === 'task'     ? form.task_id              : null,
        overhead_category_id: form.type === 'overhead' ? form.overhead_category_id : null,
        date:                 form.date,
        hours:                parseFloat(form.hours),
        description:          form.description || null,
        source: 'timesheet',
      });
      const updated = await api.get('timesheets/my-week', { week_start: weekStart });
      setEntries(updated);
      setShowAdd(null);
    } catch (e) {
      toast.error('Failed: ' + e.message);
    }
    setSaving(false);
  };

  const deleteEntry = async (id) => {
    if (!confirm('Delete this time entry?')) return;
    try {
      await api.delete(`time-entries/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      toast.error('Delete failed: ' + e.message);
    }
  };

  return (
    <div className="fade-in">

      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">My Timesheet</h2>
          <div className="text-[9px] text-gray-500">Log hours against SOW tasks or overhead categories</div>
        </div>
        <button
          onClick={() => openAdd(TODAY >= weekStart && TODAY <= weekEnd ? TODAY : weekStart)}
          className="bg-navy text-white text-[10px] font-bold px-3 py-1.5 rounded hover:opacity-90 transition-opacity"
        >
          + Log Time
        </button>
      </div>

      {/* Week navigator */}
      <div className="bg-white border border-gray-200 rounded px-3 py-2 mb-3 flex items-center gap-3">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="text-[10px] font-semibold text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50"
        >
          ‹ Prev
        </button>
        <div className="flex-1 text-center">
          <span className="text-xs font-bold text-navy">{weekLabel}</span>
          {isThisWeek && (
            <span className="ml-2 text-[8px] bg-gold/20 text-gold font-bold px-1.5 py-0.5 rounded">This Week</span>
          )}
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="text-[10px] font-semibold text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50"
        >
          Next ›
        </button>
        {!isThisWeek && (
          <button
            onClick={() => setWeekStart(getMondayOfWeek())}
            className="text-[9px] text-gold underline font-semibold"
          >
            Today
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        <SummaryCard label="Total Hours"  value={`${totalHours.toFixed(1)}h`} />
        <SummaryCard label="Days Equiv."  value={`${(totalHours / 7.5).toFixed(1)}d`} />
        <SummaryCard label="Entries"      value={entries.length} />
      </div>

      {/* Day-by-day list */}
      {loading ? (
        <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-2">
          {entriesByDay.map(({ date, entries: dayEntries, total }) => {
            const isToday   = date === TODAY;
            const isWeekend = new Date(date + 'T12:00:00').getDay() >= 6;
            const muted     = isWeekend && dayEntries.length === 0;
            return (
              <div
                key={date}
                className={`bg-white border rounded overflow-hidden transition-opacity ${isToday ? 'border-gold' : 'border-gray-200'} ${muted ? 'opacity-40' : ''}`}
              >
                {/* Day header */}
                <div className={`flex items-center justify-between px-3 py-2 border-b border-gray-100 ${isToday ? 'bg-gold/10' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-navy">{fmtFull(date)}</span>
                    {isToday && (
                      <span className="text-[7px] bg-navy text-white px-1.5 py-0.5 rounded font-bold uppercase">Today</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {total > 0 && (
                      <span className={`text-[9px] font-bold ${total >= 7 ? 'text-emerald-600' : total >= 4 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {total.toFixed(1)}h
                      </span>
                    )}
                    <button
                      onClick={() => openAdd(date)}
                      className="text-[9px] font-bold text-gold border border-gold/30 rounded px-1.5 py-0.5 hover:bg-gold/10 transition-colors"
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* Entries */}
                {dayEntries.length === 0 ? (
                  <div className="px-3 py-2 text-[9px] text-gray-300 italic">No time logged</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {dayEntries.map(e => (
                      <EntryRow key={e.id} entry={e} onDelete={deleteEntry} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add entry modal */}
      {showAdd && (
        <Modal title="Log Time" onClose={() => setShowAdd(null)}>
            <p className="text-[9px] text-gray-400 mb-3">{fmtFull(showAdd)}</p>

            {/* Type toggle */}
            <div className="flex gap-1 mb-3 bg-gray-100 rounded p-0.5">
              {[['task', 'SOW Task'], ['overhead', 'Overhead']].map(([t, l]) => (
                <button
                  key={t}
                  onClick={() => setForm(f => ({ ...f, type: t, task_id: '', overhead_category_id: '' }))}
                  className={`flex-1 text-[10px] font-bold py-1 rounded transition-colors ${form.type === t ? 'bg-white text-navy shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {/* Task or overhead picker */}
            {form.type === 'task' ? (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide">Task *</label>
                  <label className="flex items-center gap-1.5 cursor-pointer" title="Show SOW tasks from all teams, not just yours">
                    <input
                      type="checkbox"
                      checked={allTeams}
                      onChange={e => { setAllTeams(e.target.checked); setForm(f => ({ ...f, task_id: '' })); }}
                      className="accent-gold w-3 h-3"
                    />
                    <span className="text-[8px] text-gray-500 font-medium">All teams</span>
                  </label>
                </div>
                <select
                  value={form.task_id}
                  onChange={e => setForm(f => ({ ...f, task_id: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] text-navy focus:outline-none focus:border-gold"
                >
                  <option value="">— Select a task —</option>
                  {Object.entries(tasksBySow).map(([sowLabel, tasks]) => (
                    <optgroup key={sowLabel} label={sowLabel}>
                      {tasks.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.team_name ? ` (${t.team_name})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {sowTasks.length === 0 && (
                  <p className="text-[9px] text-amber-600 mt-1">
                    No active SOW tasks {allTeams ? 'found' : 'for your team — tick "All teams" to see all'}.
                  </p>
                )}
              </div>
            ) : (
              <div className="mb-3">
                <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Overhead category *</label>
                <OverheadCategorySelect
                  categories={overheads}
                  value={form.overhead_category_id}
                  onChange={v => setForm(f => ({ ...f, overhead_category_id: v }))}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] text-navy focus:outline-none focus:border-gold"
                />
              </div>
            )}

            {/* Hours */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide">Hours *</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, hours: '7.5' }))}
                    className={`text-[8px] font-bold px-2 py-0.5 rounded border transition-colors ${form.hours === '7.5' ? 'bg-navy text-white border-gold' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                  >
                    Full day
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, hours: '3.75' }))}
                    className={`text-[8px] font-bold px-2 py-0.5 rounded border transition-colors ${form.hours === '3.75' ? 'bg-navy text-white border-gold' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                  >
                    Half day
                  </button>
                </div>
              </div>
              <input
                type="number"
                min="0.25"
                max="24"
                step="0.25"
                value={form.hours}
                onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. 7.5"
              />
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Notes (optional)</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] h-16 resize-none focus:outline-none focus:border-gold"
                placeholder="What did you work on?"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowAdd(null)} className="flex-1 text-[11px] border border-gray-200 rounded py-2 font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
              <button onClick={submitEntry} disabled={saving} className="flex-1 text-[11px] bg-navy text-white rounded py-2 font-bold hover:opacity-90 disabled:opacity-50">
                {saving ? 'Saving…' : 'Log Time'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
      <div className="text-lg font-extrabold text-navy">{value}</div>
    </div>
  );
}

function EntryRow({ entry: e, onDelete }) {
  const isTask = Boolean(e.task_id);
  return (
    <div className="flex items-center px-3 py-2 group hover:bg-gray-50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${isTask ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
            {isTask ? 'SOW' : 'OH'}
          </span>
          <span className="text-[10px] font-semibold text-navy truncate">
            {isTask ? e.task_name : (e.overhead_category_name || 'Overhead')}
          </span>
          {e.sow_reference && (
            <span className="text-[8px] text-gray-400 font-medium">{e.sow_reference}</span>
          )}
        </div>
        {e.description && (
          <div className="text-[9px] text-gray-400 mt-0.5 pl-5 truncate">{e.description}</div>
        )}
      </div>
      <div className="flex items-center gap-2 ml-2 shrink-0">
        <span className="text-[12px] font-extrabold text-navy">{e.hours}h</span>
        {/* Written from approved leave: the leave request is what removes it. */}
        {e.leave_request_id ? (
          <span className="text-[9px] text-gray-400" title="From approved leave. Change or cancel the leave request and this follows it.">From leave</span>
        ) : (
          <button
            onClick={() => onDelete(e.id)}
            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-[10px] w-4 h-4 flex items-center justify-center rounded transition-opacity"
            title="Delete entry"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function blankForm(date) {
  return { type: 'task', task_id: '', overhead_category_id: '', hours: '', description: '', date };
}
